import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { FormField } from '@/components/forms/FormField';
import { AgreementTemplatesManager } from '@/components/settings/AgreementTemplatesManager';
import { AgreementLivePreview } from '@/components/agreement/AgreementLivePreview';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { useSignatures } from '@/composables/shared/useSignatures';
import { useAgreementTemplates } from '@/composables/financial/useAgreements';
import { agreementSettingsSchema, type AgreementSettingsFormData } from '@/lib/validators';
import type { OrganizationSettings } from '@/types/table';
import { NumberPreview } from './_shared';

/** Agreement tab — numbering marker, legal defaults, templates manager, live preview. */
export function AgreementTab() {
  const { settings, update } = useOrganizationSettings();
  const { defaultSignature } = useSignatures();
  const { templates } = useAgreementTemplates();

  const form = useForm<AgreementSettingsFormData>({
    resolver: zodResolver(agreementSettingsSchema),
    defaultValues: {
      agreementPrefix: 'AGR',
      nextAgreementSequence: 1,
      lateFeePercent: 2,
      clientInactivityDays: 15,
      freeSupportDays: 30,
      deliverableReviewDays: 7,
      governingCountry: 'India',
      jurisdictionLocation: 'Bangalore, Karnataka',
    },
  });

  useEffect(() => {
    if (!settings) return;
    form.reset({
      agreementPrefix: settings.agreementPrefix ?? 'AGR',
      nextAgreementSequence: settings.nextAgreementSequence ?? 1,
      lateFeePercent: Number(settings.lateFeePercent ?? 2),
      clientInactivityDays: settings.clientInactivityDays ?? 15,
      freeSupportDays: settings.freeSupportDays ?? 30,
      deliverableReviewDays: settings.deliverableReviewDays ?? 7,
      governingCountry: settings.governingCountry ?? 'India',
      jurisdictionLocation: settings.jurisdictionLocation ?? 'Bangalore, Karnataka',
    });
  }, [settings, form]);

  const values = form.watch();
  const onSubmit = (data: AgreementSettingsFormData) => update.mutate(data as Partial<OrganizationSettings>);

  // Preview uses the built-in SDA, filled with org settings + this tab's
  // policy defaults + a sample client, so the user sees how their settings
  // shape a real agreement.
  const sdaTemplate = useMemo(() => templates.find((t) => t.kind === 'SDA') ?? templates[0], [templates]);

  const previewValues = useMemo(() => {
    const orgAddress = settings ? [settings.addressLine1, settings.addressLine2, [settings.city, settings.state, settings.postalCode].filter(Boolean).join(', '), settings.country].filter(Boolean).join('\n') : '';
    return {
      COMPANY_LEGAL_NAME: settings?.companyName,
      COMPANY_EMAIL: settings?.companyEmail,
      COMPANY_IDENTIFIER: settings?.taxId,
      COMPANY_IDENTIFIER_LABEL: settings?.companyIdentifierLabel,
      COMPANY_ADDRESS: orgAddress,
      AUTHORIZED_PERSON_NAME: defaultSignature?.signatoryName,
      AUTHORIZED_PERSON_DESIGNATION: defaultSignature?.signatoryTitle,
      CLIENT_LEGAL_NAME: 'Acme Industries Pvt Ltd',
      CLIENT_ADDRESS: '42 MG Road\nBengaluru, KA 560001\nIndia',
      CLIENT_REPRESENTATIVE_NAME: 'John Smith',
      CLIENT_REPRESENTATIVE_DESIGNATION: 'Director',
      CLIENT_EMAIL: 'billing@acme.com',
      AGREEMENT_DATE: new Date().toISOString().slice(0, 10),
      TOTAL_CONTRACT_VALUE: { amount: 500000, currency: 'INR' },
      PROJECT_TIMELINE: 60,
      PROJECT_SCOPE_LIST: ['Module A — backend', 'Module B — admin UI'],
      PAYMENT_MILESTONES: [
        { name: 'On signing', percent: 50, dueDate: new Date().toISOString().slice(0, 10) },
        { name: 'On delivery', percent: 50 },
      ],
      LATE_PAYMENT_PERCENTAGE: values.lateFeePercent,
      CLIENT_INACTIVITY_DAYS: values.clientInactivityDays,
      FREE_SUPPORT_DURATION: values.freeSupportDays,
      DELIVERABLE_REVIEW_DAYS: values.deliverableReviewDays,
      GOVERNING_COUNTRY: values.governingCountry,
      JURISDICTION_LOCATION: values.jurisdictionLocation,
    };
  }, [settings, defaultSignature, values]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="grid gap-6 xl:grid-cols-[1fr_minmax(0,480px)]">
        <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Agreement Numbering</CardTitle>
              <CardDescription>
                Format: <code className="text-foreground">{'{companyPrefix}-{marker}-{year}-{seq}'}</code>.
                Company prefix (<span className="font-medium text-foreground">{settings?.companyPrefix || 'none'}</span>) is set on the General tab.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="agreementPrefix" label="Agreement Type Marker" description="e.g. AGR, SDA, CONTRACT" />
                <FormField form={form} name="nextAgreementSequence" label="Next Agreement Number" type="number" description="Auto-bumps after each agreement." />
              </div>
              <NumberPreview kind="Agreement" company={settings?.companyPrefix || ''} marker={values.agreementPrefix || 'AGR'} seq={Number(values.nextAgreementSequence) || 1} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Legal & Policy Defaults</CardTitle>
              <CardDescription>Pre-fill new agreements. The user can override anything per-agreement.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="governingCountry" label="Governing Country" description="Used in the 'Governing Law' clause." />
                <FormField form={form} name="jurisdictionLocation" label="Jurisdiction" description="City + state for legal disputes." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="lateFeePercent" label="Late Payment Fee (%)" type="number" />
                <FormField form={form} name="clientInactivityDays" label="Client Inactivity (days)" type="number" description="Auto-pause after this many days of no client response." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="freeSupportDays" label="Free Support (days)" type="number" description="Free bug-fix support after delivery." />
                <FormField form={form} name="deliverableReviewDays" label="Deliverable Review (days)" type="number" description="Client review window before auto-acceptance." />
              </div>
            </CardContent>
          </Card>

          <Separator />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => form.reset()}>Reset</Button>
            <Button type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save Agreement Settings'}</Button>
          </div>

          {/* Templates managed independently of the form save */}
          <AgreementTemplatesManager />
        </div>

        <div className="min-w-0">
          <div className="sticky top-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Live Preview</CardTitle><CardDescription>Built-in SDA filled with your settings + sample client</CardDescription></CardHeader>
              <CardContent className="bg-slate-100 dark:bg-slate-900 rounded-b-lg pt-4 pb-6">
                {sdaTemplate ? (
                  <AgreementLivePreview
                    body={sdaTemplate.body}
                    variables={sdaTemplate.variables}
                    values={previewValues}
                    primaryColor={settings?.primaryColor}
                    accentColor={settings?.accentColor}
                    title="Sample — Acme website rebuild"
                    agreementDate={new Date().toISOString().slice(0, 10)}
                    kind="SDA"
                    agreementNumber={`${settings?.companyPrefix ? settings.companyPrefix + '-' : ''}${values.agreementPrefix || 'AGR'}-${new Date().getFullYear()}-000001`}
                  />
                ) : (
                  <div className="text-xs text-muted-foreground text-center py-8">No template available for preview.</div>
                )}
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground mt-2 px-1">Branding (theme, company info) comes from the General tab. Client details shown are sample data.</p>
          </div>
        </div>
      </div>
    </form>
  );
}
